import React from 'react';
import { statusInfo, fmtDate, compact, manualProgress } from '../lib/ui';
import { Donut, Ring } from '../lib/charts';
import { EmptyState } from '../lib/illustrations';
import { PlatformGlyph, IconList, IconClock, IconPlug, IconCheckCircle, IconHeart, IconSend } from '../lib/icons';

function Card({ label, value, sub, icon: IconCmp, ring }) {
  return (
    <div className="card">
      <div className="card-top">
        <div className="k">{label}</div>
        {IconCmp ? (
          <span className="card-icon">
            <IconCmp size={16} />
          </span>
        ) : null}
      </div>
      <div className="card-v-row">
        <div className="v">{value}</div>
        {ring != null ? <Ring percent={ring} size={38} /> : null}
      </div>
      {sub ? <div className="s">{sub}</div> : null}
    </div>
  );
}

export default function Dashboard({ platforms, publications, summary, navigate }) {
  const totals = (summary && summary.totals) || {};
  const connected = platforms.filter((p) => p.connected).length;
  const engagement =
    (totals.engagement && totals.engagement.likes + totals.engagement.comments + totals.engagement.shares) || 0;

  const recent = publications.slice(0, 5);
  const scheduled = publications
    .filter((p) => p.status === 'scheduled')
    .sort((a, b) => new Date(a.scheduleAt) - new Date(b.scheduleAt));

  const successRate = totals.successRate || 0;
  const connectedPct = platforms.length ? Math.round((connected / platforms.length) * 100) : 0;

  return (
    <div>
      <div className="cards">
        <Card
          label="Publicações"
          value={compact(totals.publications)}
          sub={`${totals.published || 0} publicadas`}
          icon={IconList}
        />
        <Card
          label="Agendadas"
          value={compact(totals.scheduled)}
          sub="na fila do agendador"
          icon={IconClock}
        />
        <Card
          label="Contas conectadas"
          value={`${connected}/${platforms.length}`}
          sub="plataformas ativas"
          icon={IconPlug}
          ring={connectedPct}
        />
        <Card
          label="Taxa de sucesso"
          value={`${successRate}%`}
          sub={`${totals.successes || 0} de ${totals.attempts || 0} envios`}
          icon={IconCheckCircle}
          ring={successRate}
        />
        <Card
          label="Engajamento"
          value={compact(engagement)}
          sub={`${compact(totals.engagement && totals.engagement.views)} visualizações`}
          icon={IconHeart}
        />
      </div>

      <div className="composer">
        <section className="section">
          <div className="section-head">
            <h2>
              <span className="head-ico">
                <IconList size={16} />
              </span>
              Publicações recentes
            </h2>
            <button type="button" className="btn ghost small" onClick={() => navigate('publications')}>
              Ver todas
            </button>
          </div>
          {recent.length === 0 ? (
            <EmptyState kind="doc" size={170} title="Nenhuma publicação ainda.">
              <button type="button" className="btn primary small" style={{ marginTop: 4 }} onClick={() => navigate('composer')}>
                <span className="btn-ico">
                  <IconSend size={14} />
                </span>
                Criar a primeira
              </button>
            </EmptyState>
          ) : (
            <div className="list">
              {recent.map((pub) => {
                const info = statusInfo(pub.status);
                const prog = manualProgress(pub);
                return (
                  <div className="row" key={pub.id}>
                    <div>
                      <div className="title">{pub.title || 'Sem título'}</div>
                      <div className="meta">
                        {(pub.targets || []).join(' · ')} — {pub.results ? Object.keys(pub.results).length : 0} envios
                      </div>
                    </div>
                    <div className="row" style={{ border: 'none', background: 'transparent', padding: 0 }}>
                      {prog.total > 0 && prog.pending > 0 ? (
                        <span className="badge warn">{prog.done}/{prog.total}</span>
                      ) : null}
                      <span className={`badge ${info.cls}`}>{info.label}</span>
                      <span className="meta">{fmtDate(pub.publishedAt || pub.createdAt)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <div>
          <section className="section">
            <div className="section-head">
              <h2>
                <span className="head-ico">
                  <IconPlug size={16} />
                </span>
                Plataformas
              </h2>
            </div>
            <div className="chips">
              {platforms.map((p) => (
                <span key={p.id} className={`chip ${p.connected ? 'on' : ''}`}>
                  <span className="chip-logo" style={{ background: p.color }}>
                    <PlatformGlyph id={p.id} size={14} />
                  </span>
                  {p.name}
                </span>
              ))}
            </div>
            <div className="hint" style={{ marginTop: 12 }}>
              {connected === platforms.length
                ? 'Todas as plataformas estão conectadas.'
                : `${platforms.length - connected} plataforma(s) ainda sem conta conectada.`}
            </div>
          </section>

          <section className="section">
            <div className="section-head">
              <h2>
                <span className="head-ico">
                  <IconClock size={16} />
                </span>
                Fila de agendamento
              </h2>
            </div>
            {scheduled.length === 0 ? (
              <div className="hint">Nenhuma publicação agendada.</div>
            ) : (
              <div className="list">
                {scheduled.slice(0, 4).map((pub) => (
                  <div className="row" key={pub.id}>
                    <div>
                      <div className="title">{pub.title || 'Sem título'}</div>
                      <div className="meta">{fmtDate(pub.scheduleAt)}</div>
                    </div>
                    <span className="badge warn">Agendada</span>
                  </div>
                ))}
              </div>
            )}
            <button
              type="button"
              className="btn ghost small"
              style={{ marginTop: 12 }}
              onClick={() => navigate('composer')}
            >
              <span className="btn-ico">
                <IconClock size={14} />
              </span>
              Agendar nova publicação
            </button>
          </section>

          <section className="section section-donut">
            <div className="section-head">
              <h2>
                <span className="head-ico">
                  <IconCheckCircle size={16} />
                </span>
                Sucesso dos envios
              </h2>
            </div>
            <div className="donut-wrap">
              <Donut value={successRate} size={140} label="sucesso" sub={`${totals.attempts || 0} envios`} />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
